import React, { useState } from 'react';
import { Table, Button, Input, Space, Tag, Typography, Modal, message } from 'antd';
import { EditOutlined, StockOutlined, SearchOutlined, DeleteOutlined } from '@ant-design/icons';
import { formatCurrency } from '../../utils/currency';
import axios from 'axios';
import CategoryTag from '../../components/CategoryTag';
import ProductCategorySelect from '../../components/ProductCategorySelect';

const ProductList = ({ products, loading, onEdit, onStock, onRefresh }) => {
    // const [searchText, setSearchText] = useState(''); // Removed redundant local search
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);

    // Client-side filtering removed in favor of parent filtering
    const filteredData = products;

    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const handleDelete = (id) => {
        if (isDeleting) {
            console.log('[DELETE] Already deleting, ignoring duplicate request');
            return;
        }

        Modal.confirm({
            title: 'Delete Product?',
            content: 'This action cannot be undone. Products used in invoices cannot be deleted.',
            okText: 'Yes, Delete',
            okType: 'danger',
            okButtonProps: { loading: isDeleting },
            onOk: async () => {
                if (isDeleting) {
                    console.log('[DELETE] Already deleting, ignoring duplicate modal OK');
                    return;
                }

                setIsDeleting(true);
                try {
                    const token = localStorage.getItem('token');
                    console.log(`[DELETE] Starting delete for product ID: ${id}`);
                    const response = await axios.delete(`/api/products/${id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    console.log(`[DELETE] Backend response:`, response.data);
                    message.success('Product deleted');

                    // Wait a moment for Dolibarr to process the deletion
                    console.log(`[DELETE] Waiting 500ms before refresh to allow backend processing`);
                    await new Promise(resolve => setTimeout(resolve, 500));

                    console.log(`[DELETE] Calling onRefresh to update product list`);
                    await onRefresh();
                    console.log(`[DELETE] Refresh complete`);
                } catch (error) {
                    console.error('[DELETE] Error:', error);
                    message.error(error.response?.data?.error || 'Failed to delete product');
                } finally {
                    setIsDeleting(false);
                }
            },
            onCancel: () => {
                setIsDeleting(false);
            }
        });
    };

    const handleBulkCategory = (category_id) => {
        Modal.confirm({
            title: `Assign Category to ${selectedRowKeys.length} Products?`,
            content: 'This will update all selected items.',
            onOk: async () => {
                const hide = message.loading('Updating...', 0);
                try {
                    const token = localStorage.getItem('token');
                    await axios.post('/api/products/bulk/category', {
                        product_ids: selectedRowKeys,
                        category_id
                    }, { headers: { Authorization: `Bearer ${token}` } });
                    message.success('Bulk category updated');
                    setSelectedRowKeys([]);
                    onRefresh();
                } catch (e) {
                    message.error('Bulk update failed');
                } finally {
                    hide();
                }
            }
        });
    };

    const handleBulkDelete = () => {
        Modal.confirm({
            title: `Delete ${selectedRowKeys.length} Products?`,
            content: 'This action cannot be undone. Products used in invoices cannot be deleted.',
            okText: 'Yes, Delete All',
            okType: 'danger',
            onOk: async () => {
                const hide = message.loading('Deleting...', 0);
                try {
                    const token = localStorage.getItem('token');
                    let successCount = 0;
                    let failCount = 0;

                    console.log(`[BULK DELETE] Starting bulk delete for ${selectedRowKeys.length} products`);

                    for (const id of selectedRowKeys) {
                        try {
                            await axios.delete(`/api/products/${id}`, { headers: { Authorization: `Bearer ${token}` } });
                            successCount++;
                        } catch (error) {
                            failCount++;
                            console.error(`[BULK DELETE] Failed to delete product ${id}:`, error);
                        }
                    }

                    console.log(`[BULK DELETE] Completed: ${successCount} deleted, ${failCount} failed`);

                    if (successCount > 0) {
                        message.success(`${successCount} product(s) deleted`);
                    }
                    if (failCount > 0) {
                        message.warning(`${failCount} product(s) could not be deleted (may be in use)`);
                    }

                    setSelectedRowKeys([]);

                    // Wait for backend to process deletions
                    console.log(`[BULK DELETE] Waiting 500ms before refresh to allow backend processing`);
                    await new Promise(resolve => setTimeout(resolve, 500));

                    console.log(`[BULK DELETE] Calling onRefresh to update product list`);
                    await onRefresh();
                    console.log(`[BULK DELETE] Refresh complete`);
                } catch (e) {
                    console.error('[BULK DELETE] Unexpected error:', e);
                    message.error('Bulk delete failed');
                } finally {
                    hide();
                }
            }
        });
    };

    const handleInlineCategory = async (productId, categoryId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`/api/products/${productId}/category`,
                { category_id: categoryId },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            message.success('Category updated');
            onRefresh();
        } catch (e) {
            message.error('Update failed');
        }
    };

    const columns = [
        {
            title: 'Ref',
            dataIndex: 'ref',
            key: 'ref',
            render: text => <Typography.Text strong>{text}</Typography.Text>,
            sorter: (a, b) => a.ref.localeCompare(b.ref)
        },
        {
            title: 'Label',
            dataIndex: 'label',
            key: 'label',
            sorter: (a, b) => a.label.localeCompare(b.label)
        },
        {
            title: 'Barcode',
            dataIndex: 'barcode',
            key: 'barcode',
            render: text => <Typography.Text type="secondary" style={{ fontSize: '12px' }}>{text || '-'}</Typography.Text>
        },
        {
            title: 'Category',
            dataIndex: 'category_id',
            key: 'category_id',
            width: 180,
            render: (catId, record) => (
                <div onClick={e => e.stopPropagation()}>
                    <ProductCategorySelect
                        value={catId}
                        onChange={(val) => handleInlineCategory(record.id, val)}
                        style={{ width: '100%' }}
                        size="small"
                        bordered={false}
                    />
                </div>
            )
        },
        {
            title: 'Stock',
            dataIndex: 'stock',
            key: 'stock',
            align: 'right',
            render: val => <Tag color={val > 0 ? 'blue' : 'red'}>{val}</Tag>,
            sorter: (a, b) => a.stock - b.stock
        },
        {
            title: 'Price (TTC)',
            dataIndex: 'price',
            key: 'price',
            align: 'right',
            render: val => <Typography.Text strong>{formatCurrency(val)}</Typography.Text>
        },
        {
            title: 'VAT',
            dataIndex: 'vat_rate',
            key: 'vat_rate',
            align: 'right',
            render: val => <Typography.Text type="secondary">{parseFloat(val)}%</Typography.Text>
        },
        {
            title: 'Actions', key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} size="small" onClick={() => onEdit(record)}>Edit</Button>
                    <Button icon={<StockOutlined />} size="small" onClick={() => onStock(record)}>Stock</Button>
                    <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDelete(record.id)} />
                </Space>
            )
        }
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <Space>
                    <Button onClick={onRefresh}>Refresh List</Button>
                </Space>

                <Space>
                    {selectedRowKeys.length > 0 && (
                        <Space>
                            <ProductCategorySelect
                                style={{ width: 180 }}
                                onChange={handleBulkCategory}
                                placeholder="Bulk Assign Category"
                            />
                            <Button type="primary" danger onClick={handleBulkDelete}>
                                Delete Selected ({selectedRowKeys.length})
                            </Button>
                        </Space>
                    )}
                </Space>
            </div>
            <Table
                rowSelection={rowSelection}
                dataSource={filteredData}
                columns={columns}
                rowKey="id"
                loading={loading}
                locale={{ emptyText: 'No products found' }}
                pagination={{ pageSize: 20 }}
                size="small"
                sticky
            />
        </div>
    );
};

export default ProductList;
